import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  createGame,
  GameData,
  GameOption,
  getAllGames,
  getAllGamesOptions,
  GetAllGamesRequest,
  GetAllGamesResponse,
  GetAllGamesOptionsResponse,
} from '@/application/requests/gamelist';
import { fetchAllGamesForCombobox } from '@/application/redux/thunks/fetchAllGamesComboboxThunk';

export interface GameState {
  allGames: GameData[];
  allGameOptions: GameOption[];
  allGameOptionsLoading: boolean;
  allGameOptionsLoaded: boolean;
  allGameOptionsWithInactive: GameOption[];
  allGameOptionsWithInactiveLoading: boolean;
  allGameOptionsWithInactiveLoaded: boolean;
  allGameOptionsWithDeleted: GameOption[];
  allGameOptionsWithDeletedLoading: boolean;
  allGameOptionsWithDeletedLoaded: boolean;
  allGameOptionsForTopup: GameOption[];
  allGameOptionsForTopupLoading: boolean;
  allGameOptionsForTopupLoaded: boolean;
  loading: boolean;
  error: string | null;
  games: GameData[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: GameState = {
  allGames: [],
  allGameOptions: [],
  allGameOptionsLoading: false,
  allGameOptionsLoaded: false,
  allGameOptionsWithInactive: [],
  allGameOptionsWithInactiveLoading: false,
  allGameOptionsWithInactiveLoaded: false,
  allGameOptionsWithDeleted: [],
  allGameOptionsWithDeletedLoading: false,
  allGameOptionsWithDeletedLoaded: false,
  allGameOptionsForTopup: [],
  allGameOptionsForTopupLoading: false,
  allGameOptionsForTopupLoaded: false,
  games: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
};

export const fetchAllGames = createAsyncThunk(
  'game/fetchAllGames',
  async (params: GetAllGamesRequest, { rejectWithValue }) => {
    try {
      const response: GetAllGamesResponse = await getAllGames(params);

      if (!response.success || !response.data) {
        return rejectWithValue(
          response.message || 'Failed to fetch games list'
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch games list');
    }
  }
);

export const createGameAction = createAsyncThunk(
  'game/createGame',
  async (data: GameData, { rejectWithValue }) => {
    try {
      const response = await createGame(data);

      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to create game');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create game');
    }
  }
);

export { fetchAllGamesForCombobox };

export const fetchAllGamesForComboboxWithInactive = createAsyncThunk(
  'game/fetchAllGamesForComboboxWithInactive',
  async (_, { rejectWithValue }) => {
    try {
      const response: GetAllGamesOptionsResponse = await getAllGamesOptions({
        includeInactive: true,
      });

      if (!response.success || !response.data) {
        return rejectWithValue(response.message || 'Failed to fetch all games');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all games');
    }
  }
);

export const fetchAllGamesForComboboxWithDeleted = createAsyncThunk(
  'game/fetchAllGamesForComboboxWithDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const response: GetAllGamesOptionsResponse = await getAllGamesOptions({
        includeDeleted: true,
        includeInactive: true,
      });

      if (!response.success || !response.data) {
        return rejectWithValue(response.message || 'Failed to fetch all games');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all games');
    }
  }
);

export const fetchAllGamesForComboboxForTopup = createAsyncThunk(
  'game/fetchAllGamesForComboboxForTopup',
  async (_, { rejectWithValue }) => {
    try {
      const response: GetAllGamesOptionsResponse = await getAllGamesOptions({
        forTopup: true,
      });

      if (!response.success || !response.data) {
        return rejectWithValue(response.message || 'Failed to fetch all games');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all games');
    }
  }
);

export const fetchAllGamesHandlers = {
  pending: (state: GameState) => {
    state.loading = true;
    state.error = null;
  },
  fulfilled: (
    state: GameState,
    action: PayloadAction<GetAllGamesResponse['data']>
  ) => {
    state.loading = false;
    if (action.payload) {
      state.games = action.payload.data;
      state.totalItems = action.payload.total;
      state.totalPages = action.payload.total_pages;
      state.currentPage = action.payload.page;
      state.hasNext = action.payload.has_next;
      state.hasPrevious = action.payload.has_previous;
    }
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.loading = false;
    state.error = action.payload as string;
    state.games = [];
  },
};

export const createGameHandlers = {
  pending: (state: GameState) => {
    state.loading = true;
    state.error = null;
  },
  fulfilled: (state: GameState, action: PayloadAction<GameData>) => {
    state.loading = false;
    if (action.payload) {
      state.allGames = [...state.allGames, action.payload];
    }
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.loading = false;
    state.error = action.payload as string;
  },
};

export const fetchComboboxHandlers = {
  pending: (state: GameState) => {
    state.allGameOptionsLoading = true;
  },
  fulfilled: (state: GameState, action: PayloadAction<GameOption[]>) => {
    state.allGameOptionsLoading = false;
    state.allGameOptionsLoaded = true;
    state.allGameOptions = action.payload;
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.allGameOptionsLoading = false;
    state.allGameOptionsLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

export const fetchComboboxWithInactiveHandlers = {
  pending: (state: GameState) => {
    state.allGameOptionsWithInactiveLoading = true;
  },
  fulfilled: (state: GameState, action: PayloadAction<GameOption[]>) => {
    state.allGameOptionsWithInactiveLoading = false;
    state.allGameOptionsWithInactiveLoaded = true;
    state.allGameOptionsWithInactive = action.payload;
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.allGameOptionsWithInactiveLoading = false;
    state.allGameOptionsWithInactiveLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

export const fetchComboboxWithDeletedHandlers = {
  pending: (state: GameState) => {
    state.allGameOptionsWithDeletedLoading = true;
  },
  fulfilled: (state: GameState, action: PayloadAction<GameOption[]>) => {
    state.allGameOptionsWithDeletedLoading = false;
    state.allGameOptionsWithDeletedLoaded = true;
    state.allGameOptionsWithDeleted = action.payload;
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.allGameOptionsWithDeletedLoading = false;
    state.allGameOptionsWithDeletedLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

export const fetchComboboxForTopupHandlers = {
  pending: (state: GameState) => {
    state.allGameOptionsForTopupLoading = true;
  },
  fulfilled: (state: GameState, action: PayloadAction<GameOption[]>) => {
    state.allGameOptionsForTopupLoading = false;
    state.allGameOptionsForTopupLoaded = true;
    state.allGameOptionsForTopup = action.payload;
  },
  rejected: (state: GameState, action: PayloadAction<string | unknown>) => {
    state.allGameOptionsForTopupLoading = false;
    state.allGameOptionsForTopupLoaded = true;
    if (action.payload !== 'Already loading or loaded') {
      state.error = action.payload as string;
    }
  },
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    addGame(state, action) {
      state.allGames.push(action.payload.data);
      state.allGameOptions.push({
        id: action.payload.data.id,
        name: action.payload.data.name,
      });
      state.games.push(action.payload.data);
    },
    deleteGame(state, action) {
      state.allGames = state.allGames.filter(
        (game) => game.id !== action.payload
      );
      state.allGameOptions = state.allGameOptions.filter(
        (game) => game.id !== action.payload
      );
      state.games = state.games.filter((game) => game.id !== action.payload);
    },
    editGame(state, action) {
      const updatedGame = action.payload.data;
      state.allGames = state.allGames.map((game) => {
        if (game.id === updatedGame.id) {
          return updatedGame;
        }
        return game;
      });

      state.games = state.games.map((game) => {
        if (game.id === updatedGame.id) {
          return updatedGame;
        }
        return game;
      });
      state.allGameOptions = state.allGameOptions.map((game) => {
        if (game.id === updatedGame.id) {
          return { id: updatedGame.id, name: updatedGame.name };
        }
        return game;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Games
      .addCase(fetchAllGames.pending, fetchAllGamesHandlers.pending)
      .addCase(fetchAllGames.fulfilled, fetchAllGamesHandlers.fulfilled)
      .addCase(fetchAllGames.rejected, fetchAllGamesHandlers.rejected)

      // Fetch Combobox
      .addCase(fetchAllGamesForCombobox.pending, fetchComboboxHandlers.pending)
      .addCase(
        fetchAllGamesForCombobox.fulfilled,
        fetchComboboxHandlers.fulfilled
      )
      .addCase(
        fetchAllGamesForCombobox.rejected,
        fetchComboboxHandlers.rejected
      )

      // Fetch Combobox (include inactive)
      .addCase(
        fetchAllGamesForComboboxWithInactive.pending,
        fetchComboboxWithInactiveHandlers.pending
      )
      .addCase(
        fetchAllGamesForComboboxWithInactive.fulfilled,
        fetchComboboxWithInactiveHandlers.fulfilled
      )
      .addCase(
        fetchAllGamesForComboboxWithInactive.rejected,
        fetchComboboxWithInactiveHandlers.rejected
      )

      // Fetch Combobox (include deleted + inactive)
      .addCase(
        fetchAllGamesForComboboxWithDeleted.pending,
        fetchComboboxWithDeletedHandlers.pending
      )
      .addCase(
        fetchAllGamesForComboboxWithDeleted.fulfilled,
        fetchComboboxWithDeletedHandlers.fulfilled
      )
      .addCase(
        fetchAllGamesForComboboxWithDeleted.rejected,
        fetchComboboxWithDeletedHandlers.rejected
      )

      // Fetch Combobox (for topup)
      .addCase(
        fetchAllGamesForComboboxForTopup.pending,
        fetchComboboxForTopupHandlers.pending
      )
      .addCase(
        fetchAllGamesForComboboxForTopup.fulfilled,
        fetchComboboxForTopupHandlers.fulfilled
      )
      .addCase(
        fetchAllGamesForComboboxForTopup.rejected,
        fetchComboboxForTopupHandlers.rejected
      );
  },
});

export const { setCurrentPage, setPageSize, addGame, deleteGame, editGame } =
  gameSlice.actions;
export default gameSlice.reducer;
